/**
 * weather_audio — the PURE mix behind the weather sound loops (2026-09-11,
 * "add sounds to the weather"). The clips are Higgsfield text-to-audio beds
 * (tools/asset-pipeline/gen-ambience.mjs): light rain, heavy rain, storm
 * wind, snow wind, sandstorm — crossfaded by intensity, wind and dust; two
 * more thunder claps join the existing one, each delayed by distance.
 *
 * Research anchors: rain beds are layered by intensity (a light patter under
 * a heavy roar, never one clip at two volumes); fresh snow absorbs 60–90 % of
 * ambient sound, so snow is QUIET — wind and a fine hiss, nothing else; a
 * sandstorm is a 50–500 Hz roar with the hiss of grains; thunder arrives
 * after the flash at 3 s per kilometre.
 */

export interface WeatherAudioInput {
  kind: 'none' | 'rain' | 'snow' | 'dust';
  /** Intensity 0..1 (rain/snow precipitation, or the dust for a sandstorm). */
  density: number;
  /** Wind 0..1. */
  wind: number;
  /** Inside a building / a ship interior (walls muffle the weather). */
  indoors: boolean;
  /** Under water (nothing of the weather is heard). */
  submerged: boolean;
}

export interface WeatherAudioMix {
  weather_rain_light: number;
  weather_rain_heavy: number;
  weather_storm_wind: number;
  weather_snow_wind: number;
  weather_sandstorm: number;
}

export const WEATHER_LOOP_KEYS: Array<keyof WeatherAudioMix> = ['weather_rain_light', 'weather_rain_heavy', 'weather_storm_wind', 'weather_snow_wind', 'weather_sandstorm'];
/** Walls take this much of the weather. */
export const INDOOR_MUFFLE = 0.3;
/** Master level of the weather bed under the sfx bus. */
export const WEATHER_BED_GAIN = 0.55;

const smooth = (e0: number, e1: number, v: number): number => { const t = Math.max(0, Math.min(1, (v - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };

/** Loop gains for the current weather. */
export function weatherAudioMix(i: WeatherAudioInput): WeatherAudioMix {
  const mix: WeatherAudioMix = { weather_rain_light: 0, weather_rain_heavy: 0, weather_storm_wind: 0, weather_snow_wind: 0, weather_sandstorm: 0 };
  if (i.submerged) return mix;
  const d = Math.max(0, Math.min(1, i.density));
  if (i.kind === 'rain') {
    // A light patter rises first, the heavy roar takes over past moderate rain,
    // and the wind bed comes in only for a storm (with the wind itself).
    mix.weather_rain_light = smooth(0.02, 0.35, d) * (1 - smooth(0.55, 0.95, d) * 0.6);
    mix.weather_rain_heavy = smooth(0.4, 0.95, d);
    mix.weather_storm_wind = smooth(0.7, 1, d) * (0.4 + 0.6 * i.wind);
  } else if (i.kind === 'snow') {
    mix.weather_snow_wind = smooth(0.02, 0.4, d) * (0.35 + 0.65 * i.wind);
  } else if (i.kind === 'dust') {
    mix.weather_sandstorm = smooth(0.03, 0.6, d);
  }
  const k = WEATHER_BED_GAIN * (i.indoors ? INDOOR_MUFFLE : 1);
  for (const key of WEATHER_LOOP_KEYS) mix[key] *= k;
  return mix;
}

export const THUNDER_KEYS = ['sfx_thunder', 'sfx_thunder_2', 'sfx_thunder_3'] as const;

/**
 * A thunder clap for a flash: the strike's distance (km, from a hash) sets the
 * delay (3 s per km) and the loudness; a close strike is the sharp crack.
 */
export function thunderFor(hash01: number, indoors: boolean): { key: string; delayS: number; gain: number; rate: number } {
  const km = 0.3 + hash01 * 3.2;
  const delayS = km * 3;
  const gain = (0.85 - 0.18 * km) * (indoors ? 0.5 : 1);
  const key = km < 1.0 ? 'sfx_thunder_3' : km < 2.2 ? 'sfx_thunder' : 'sfx_thunder_2';
  return { key, delayS, gain: Math.max(0.15, gain), rate: 0.9 + ((hash01 * 7.31) % 1) * 0.2 };
}
